import { Alert, Avatar, Button } from "@mui/material"
import classNames from "classnames"
import ExternalLinks from "components/link"
import Markdown from "markdown-to-jsx"
import PropTypes from "prop-types"
import { memo, useCallback, useState } from "react"
import { MESSAGE_ROLES } from "../../helpers/constants"
import { RefreshableError, RetryableError } from "../../helpers/errors"
import { MESSAGE } from "../../helpers/shapes"
import { useAiTeammateContext, useMessagesContext } from "../../hooks/context"
import useSmoothRevealText, { FINISHED_ANSWER_TUNING } from "../../hooks/use-smooth-reveal-text"
import AssistantIcon from "../icons/assistant"
import UserIcon from "../icons/user"
import PlotArtifact from "../plot-artifact"
import TableArtifact from "../table-artifact"
import { AiTeammateTurnSteps, countToolCalls, formatToolCallCount, TIMELINE_PART_SHAPE, TrainOfThoughtStrip } from "../thinking"
import MessageTime from "./message-time"
import "./style.scss"

function AiTeammateMessage({ message, messageTimeRefresher, revealFromStart, onRevealComplete, steps }) {
  const { role, text, createdAt, error, artifacts, code } = message || {}
  const { setError, currency, isManagedAgent } = useAiTeammateContext()
  const { refetch, dispatchUserMessageInputOverride, lastUserMessageText } = useMessagesContext()
  const [hasFullyRevealed, setHasFullyRevealed] = useState(false)
  const isRevealing = !!revealFromStart && !hasFullyRevealed
  const handleRevealComplete = useCallback(() => {
    setHasFullyRevealed(true)
    onRevealComplete?.()
  }, [onRevealComplete])
  const displayText = useSmoothRevealText(text || "", { isStreaming: isRevealing, tuning: FINISHED_ANSWER_TUNING, onComplete: handleRevealComplete })

  if (!message) {
    return null
  }

  const icon = role === MESSAGE_ROLES.USER ? <UserIcon /> : <AssistantIcon />

  const stepsToolCallCount = countToolCalls(steps ?? [])
  const stepsSummary = steps?.length > 0
    ? `Worked through ${steps.length} ${steps.length === 1 ? "step" : "steps"}`
      + (stepsToolCallCount > 0 ? ` \u2014 ${formatToolCallCount(stepsToolCallCount)}` : "")
    : ""

  function handleRetry() {
    dispatchUserMessageInputOverride(lastUserMessageText)
  }

  function handleRefresh() {
    if (lastUserMessageText) {
      dispatchUserMessageInputOverride(lastUserMessageText)
    }
    setError(null)
    refetch()
  }

  let content = <div className="message-container">
    {steps?.length > 0 && <TrainOfThoughtStrip status={stepsSummary} className="turn-steps">
      <AiTeammateTurnSteps parts={steps} />
    </TrainOfThoughtStrip>}
    <div className="message-content">
      <ExternalLinks>
        {role === MESSAGE_ROLES.USER ? displayText : <Markdown>{displayText}</Markdown>}
      </ExternalLinks>

      {code && <span className="message-code">[{code}]</span>}

      {artifacts && artifacts.map(artifact => {
        switch (artifact.type) {
          case "table":
            return <TableArtifact key={artifact.id} artifact={artifact} currency={currency} />
          case "plot":
            return <PlotArtifact key={artifact.id} artifact={artifact} />
          default:
            return null
        }
      })}
    </div>
    {createdAt && <MessageTime date={createdAt} messageTimeRefresher={messageTimeRefresher} />}
  </div>

  if (error) {
    let action
    if (error instanceof RetryableError) {
      action = <Button onClick={handleRetry}>Retry</Button>
    } else if (error instanceof RefreshableError) {
      action = <Button onClick={handleRefresh}>Refresh</Button>
    }

    content = <Alert severity="error" className="error-message" action={action}>
      {error.message}
    </Alert>
  }

  return <>
    <div className={classNames("message", role, { error: !!error })}>
      {content}
      {!isManagedAgent && <Avatar className="message-avatar">
        {icon}
      </Avatar>}
    </div>
  </>
}

AiTeammateMessage.propTypes = {
  message: MESSAGE,
  messageTimeRefresher: PropTypes.number,
  revealFromStart: PropTypes.bool,
  onRevealComplete: PropTypes.func,
  steps: PropTypes.arrayOf(TIMELINE_PART_SHAPE),
}

export default memo(AiTeammateMessage)
